import React from 'react';

import { Text } from '@react-pdf/renderer';
import DOMPurify from 'dompurify';

import { getPlainTextFromHtml } from '@shared/helpers';

const currencySymbols = /[₽€$₼£ƒ₾₹₸¥₮₴₣]/;
const mathSymbols = /[⊆⊂⊇⊃∑∫∞√≈≠≤≥∂∀∃∈∉∪∩∧∨⇒⇔←↑→↓±÷×]/;

const getFontFamilyForChar = (char: string): string | undefined => {
    if (currencySymbols.test(char)) return 'NotoSans';
    if (mathSymbols.test(char)) return 'DejaVuSans';
    return undefined;
};

export const renderTextWithSpecialFont = (text: string, baseStyle: any = {}) => {
    const parts: React.ReactNode[] = [];
    let buffer = '';
    let bufferFont: string | undefined = getFontFamilyForChar(text[0]) || baseStyle.fontFamily;

    const flushBuffer = (font: string | undefined, key: string) => {
        if (!buffer.length) return;
        parts.push(
            <Text key={key} style={{ ...baseStyle, fontFamily: font }}>
                {buffer}
            </Text>
        );
        buffer = '';
    };

    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const font = getFontFamilyForChar(char) || baseStyle.fontFamily;

        if (font !== bufferFont) {
            flushBuffer(bufferFont, `part-${i}`);
            bufferFont = font;
        }

        buffer += char;
    }

    flushBuffer(bufferFont, `part-end`);

    return parts;
};
export const renderRichText = (html: string) => {
    const regex = /<span style="(color:(#[0-9a-fA-F]{6})|font-weight:\s*bold\s*;)">(.*?)<\/span>/gi;

    const parts: (string | { color?: string; bold?: boolean; text: string })[] = [];

    let lastIndex = 0;
    let match;
    while ((match = regex.exec(html)) !== null) {
        if (match.index > lastIndex) {
            parts.push(html.slice(lastIndex, match.index));
        }

        if (match[2]) {
            parts.push({ color: match[2], text: match[3] });
        } else if (/font-weight/i.test(match[1])) {
            parts.push({ bold: true, text: match[3] });
        }

        lastIndex = regex.lastIndex;
    }

    if (lastIndex < html.length) {
        parts.push(html.slice(lastIndex));
    }

    return (
        <Text>
            {parts.map((p, i) => {
                if (typeof p === 'string') {
                    const clean = DOMPurify.sanitize(p, {
                        ALLOWED_TAGS: [],
                        ALLOWED_ATTR: [],
                    });
                    return (
                        <React.Fragment key={i}>{renderTextWithSpecialFont(clean)}</React.Fragment>
                    );
                }

                const plain = getPlainTextFromHtml(p.text);
                const style: any = {};

                if (p.color) style.color = p.color;
                if (p.bold) style.fontWeight = 'bold';

                return (
                    <React.Fragment key={i}>
                        {renderTextWithSpecialFont(plain, style)}
                    </React.Fragment>
                );
            })}
        </Text>
    );
};
